import React, { useMemo, useCallback } from 'react';
import { isEqual } from 'lodash';
import { useFilter } from 'js/hooks/useFilter';
import { ElementTrendSet } from './ElementTrendSet';
import CapabilityTrendsLegend from './CapabilityTrendsLegend';
import styles from './CapabilityTrendsChart.module.scss';

function generateCapabilityFilter(capabilityId) {
  return { type: 'capability', criteria: { capabilityId } };
}

function filterHandler(filterLevel, filterGenerator, filterSetter) {
  return (...args) => {
    filterSetter(filterLevel, filterGenerator(...args));
  };
}

const filterLayers = ['transient', 'locked'];

/**
 * Trend charts with a legend that highlights a line on hover and locks it on
 * click. `trends` are `{ element, capabilityTrends }`, where each trend line
 * is keyed by an id `items` names (a capability, or a standard question).
 */
export function TrendSetWithLegend({ trends, metrics, items }) {
  const paletteMap = useMemo(
    () =>
      Object.keys(items ?? {}).reduce((acc, id, i) => {
        acc[id] = { className: `palette-chart-dark-${i + 1}` };
        return acc;
      }, {}),
    [items]
  );

  const { setFilter, clearFilter, toggleFilter, activeFilter, filterStack } =
    useFilter(filterLayers, isEqual);

  const handleSetTransientFilter = useMemo(
    () => filterHandler('transient', generateCapabilityFilter, setFilter),
    [setFilter]
  );

  const handleToggleLockedFilter = useMemo(
    () => filterHandler('locked', generateCapabilityFilter, toggleFilter),
    [toggleFilter]
  );

  const clearTransientFilter = useCallback(() => {
    clearFilter('transient');
  }, [clearFilter]);

  return (
    <div className={styles['capability-trends-interface']}>
      <div className={styles['capability-trends']}>
        <div className={styles['capability-trends-wrapper']}>
          <ElementTrendSet
            filter={activeFilter}
            trends={trends}
            metrics={metrics}
            paletteMap={paletteMap}
            onHoverTrend={handleSetTransientFilter}
            onUnhoverTrend={clearTransientFilter}
            onClickTrend={handleToggleLockedFilter}
          />
        </div>
      </div>
      <div className={styles['capability-trends-legend']}>
        <CapabilityTrendsLegend
          paletteMap={paletteMap}
          capabilities={items}
          filterStack={filterStack}
          onHoverCapability={handleSetTransientFilter}
          onUnhoverCapability={clearTransientFilter}
          onClickCapability={handleToggleLockedFilter}
        />
      </div>
    </div>
  );
}

export default TrendSetWithLegend;
